import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { FilterBar, FilterSelect, FilterSearch } from '@/components/feature/FilterBar';

type ResourceType = 'Flashcards' | 'Cheat Sheet';

interface Resource {
  id: string;
  title: string;
  description: string;
  type: ResourceType;
  topic: string;
  difficulty: string;
  fileUrl?: string;
  frontText?: string;
  backText?: string;
  size?: string;
}

const typeColors: Record<string, string> = {
  'Flashcards': 'bg-violet-100 text-violet-700',
  'Cheat Sheet': 'bg-sky-100 text-sky-700',
};

const typeIcons: Record<string, string> = {
  'Flashcards': 'ri-stack-line',
  'Cheat Sheet': 'ri-clipboard-line',
};

const difficultyColors: Record<string, string> = {
  Foundation: 'bg-emerald-100 text-emerald-700',
  Higher: 'bg-orange-100 text-orange-700',
  Both: 'bg-slate-100 text-slate-600',
};

export default function ALevelResourcesPage() {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState<string>('All');
  const [selectedType, setSelectedType] = useState<ResourceType | 'All'>('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewResource, setPreviewResource] = useState<Resource | null>(null);

  const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [csRes, fcRes] = await Promise.all([
          fetch(`${API_BASE}/api/cheatsheets`),
          fetch(`${API_BASE}/api/flashcards`)
        ]);

        let combined: Resource[] = [];

        if (csRes.ok) {
          const csData = await csRes.json();
          combined = [...combined, ...csData.filter((i: any) => i.level === 'A-Level' || i.level === 'AS-Level').map((i: any) => ({
            id: i.id || i._id,
            title: i.title,
            description: i.description || 'Quick revision sheet',
            type: 'Cheat Sheet' as const,
            topic: i.subject || 'General',
            difficulty: 'Both',
            fileUrl: i.fileUrl,
            size: i.size
          }))];
        }

        if (fcRes.ok) {
          const fcData = await fcRes.json();
          combined = [...combined, ...fcData.filter((i: any) => i.level === 'A-Level' || i.level === 'AS-Level').map((i: any) => ({
            id: i.id || i._id,
            title: i.title,
            description: i.description || 'Active recall cards',
            type: 'Flashcards' as const,
            topic: i.subject || 'General',
            difficulty: 'Both',
            frontText: i.frontText,
            backText: i.backText
          }))];
        }

        setResources(combined);
      } catch (err) {
        console.error('Failed to fetch resources:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [API_BASE]);

  const filtered = resources.filter((r) => {
    if (selectedTopic !== 'All' && r.topic !== selectedTopic) return false;
    if (selectedType !== 'All' && r.type !== selectedType) return false;
    if (selectedDifficulty !== 'All' && r.difficulty !== selectedDifficulty) return false;
    if (searchQuery && !r.title.toLowerCase().includes(searchQuery.toLowerCase()) && !r.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const featured = filtered.slice(0, 4);
  const topics = ['All', ...Array.from(new Set(resources.map(r => r.topic)))];

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      <section className="relative bg-slate-900 pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20background%20with%20open%20books%2C%20notes%20and%20study%20materials%2C%20deep%20navy%20gradient%2C%20geometric%20patterns%2C%20clean%20minimal%20academic%20design%2C%20subtle%20warm%20lighting%20on%20dark%20background&width=1440&height=280&seq=alres1&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/85 to-slate-900/60"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">A Level Resources</span>
            <i className="ri-arrow-right-s-line text-slate-500 text-xs"></i>
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Resources</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>
            A Level Revision Resources
          </h1>
          <p className="text-slate-400 text-base max-w-2xl">
            Access Cheat Sheets and Flashcards for A-Level and AS-Level Computer Science.
          </p>
        </div>
      </section>

      {/* Featured */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Latest Additions</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((r) => (
              <button key={r.id} onClick={() => setPreviewResource(r)} className="flex items-center gap-3 p-3 rounded-xl border border-emerald-100 bg-emerald-50 cursor-pointer hover:border-emerald-300 transition-colors group text-left">
                <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                  <i className={`${typeIcons[r.type]} text-emerald-600 text-base`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-800 leading-snug truncate">{r.title}</p>
                  <p className="text-xs text-emerald-600 font-medium mt-0.5">{r.type}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <FilterBar count={filtered.length} countLabel="resources">
        <FilterSelect
          label="Topic"
          value={selectedTopic}
          onChange={(v) => setSelectedTopic(v)}
          options={topics.map((t) => ({ value: t, label: t === 'All' ? 'All Topics' : t }))}
        />
        <FilterSelect
          label="Type"
          value={selectedType}
          onChange={(v) => setSelectedType(v as ResourceType | 'All')}
          options={[{ value: 'All', label: 'All Types' }, { value: 'Flashcards', label: 'Flashcards' }, { value: 'Cheat Sheet', label: 'Cheat Sheet' }]}
        />
        <div className="sm:ml-auto">
          <FilterSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search resources..."
            width="sm:w-60"
          />
        </div>
      </FilterBar>

      <main className="flex-1 max-w-7xl mx-auto px-4 md:px-6 py-10 w-full">
        {loading ? (
             <div className="flex items-center justify-center py-20">
                <i className="ri-loader-4-line animate-spin text-3xl text-emerald-600"></i>
             </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-2xl mb-4">
              <i className="ri-search-line text-slate-400 text-2xl"></i>
            </div>
            <p className="text-slate-600 font-medium mb-1">No resources found</p>
            <p className="text-slate-400 text-sm">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-100 p-5 hover:border-emerald-200 transition-colors group">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-slate-50 rounded-xl flex-shrink-0 group-hover:bg-emerald-50 transition-colors">
                    <i className={`${typeIcons[r.type]} text-slate-500 group-hover:text-emerald-600 text-xl transition-colors`}></i>
                  </div>
                  <div className="flex flex-col items-end gap-1.5">
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${typeColors[r.type]}`}>{r.type}</span>
                  </div>
                </div>
                <h3 className="text-sm font-bold text-slate-800 mb-2 leading-snug">{r.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed mb-4 line-clamp-2">{r.description}</p>
                <div className="flex items-center gap-3 text-xs text-slate-400 mb-4">
                  <span className="flex items-center gap-1"><i className="ri-bookmark-line"></i>{r.topic}</span>
                  {r.size && <span className="flex items-center gap-1"><i className="ri-file-line"></i>{r.size}</span>}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewResource(r)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-eye-line text-sm"></i>
                    Preview
                  </button>
                  <button 
                    onClick={() => {
                        if (r.type === 'Cheat Sheet' && r.id) {
                            window.open(`${API_BASE}/api/cheatsheets/proxy/${r.id}`, '_blank');
                        }
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${r.type === 'Flashcards' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    disabled={r.type === 'Flashcards'}
                  >
                    <i className="ri-download-line text-sm"></i>
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Preview Modal */}
      {previewResource && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPreviewResource(null)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          <div className="relative bg-white rounded-2xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center bg-emerald-50 rounded-xl flex-shrink-0">
                  <i className={`${typeIcons[previewResource.type]} text-emerald-600 text-lg`}></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 leading-snug">{previewResource.title}</h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[previewResource.type]}`}>{previewResource.type}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setPreviewResource(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-slate-500 text-lg"></i>
              </button>
            </div>

            <div className="px-6 py-5">
              <p className="text-sm text-slate-600 leading-relaxed mb-5">{previewResource.description}</p>
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 mb-5">
                <div className="flex items-center gap-2 mb-4">
                  <i className={`ri-${previewResource.type === 'Flashcards' ? 'chat-4' : 'file-text'}-line text-slate-400 text-sm`}></i>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    {previewResource.type === 'Flashcards' ? 'Interactive Card' : 'Document Preview'}
                  </span>
                </div>
                
                {previewResource.type === 'Flashcards' ? (
                    <div className="space-y-4">
                        <div className="p-4 bg-white rounded-lg border border-slate-100 shadow-sm">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Front Side</p>
                            <p className="text-sm font-medium text-slate-700">{previewResource.frontText || 'No front text'}</p>
                        </div>
                        <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-100 shadow-sm">
                            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest mb-2">Back Side (Answer)</p>
                            <p className="text-sm font-medium text-slate-800">{previewResource.backText || 'No back text'}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="space-y-2.5">
                        <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                        <div className="h-3 bg-slate-100 rounded w-full"></div>
                        <div className="h-3 bg-slate-100 rounded w-5/6"></div>
                        <div className="h-3 bg-slate-100 rounded w-full"></div>
                        <div className="h-3 bg-slate-100 rounded w-4/5"></div>
                        <div className="h-4 bg-slate-200 rounded w-2/3 mt-4"></div>
                        </div>
                        <div className="mt-4 flex items-center justify-center py-3 bg-white/80 rounded-lg border border-slate-200">
                        <p className="text-xs text-slate-400 flex items-center gap-1.5">
                            <i className="ri-lock-line text-xs"></i>
                            Full content available after download
                        </p>
                        </div>
                    </>
                )}
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setPreviewResource(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap">
                Close
              </button>
              {previewResource.type === 'Cheat Sheet' && (
                  <button 
                  onClick={() => window.open(`${API_BASE}/api/cheatsheets/proxy/${previewResource.id}`, '_blank')}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-download-line text-sm"></i>
                    Download Free
                  </button>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
